import { z } from 'zod';
import { LangCodeSchema } from './primitives';

// =============================================================================
// Search entity types
// =============================================================================
// Core entity_type values used by search_index triggers in migration 0007.
// Addons may push additional entity_type values — the Edge Function validates
// them permissively as plain strings on the query side.

export const SearchEntityTypeSchema = z.enum([
  'user',
  'team',
  'folder',
  'document',
  'channel',
  'message',
]);
export type SearchEntityType = z.infer<typeof SearchEntityTypeSchema>;

// =============================================================================
// SearchResult
// =============================================================================

/** Single hit returned by GET /search. */
export const SearchResultSchema = z.object({
  entity_type: z.string(), // widened from enum to accept addon-provided types
  entity_id: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  body_snippet: z.string().nullable(),
  /** `ts_rank_cd` score; higher = more relevant. */
  rank: z.number(),
  url: z.string().nullable(),
  source_lang: LangCodeSchema,
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

// =============================================================================
// SearchQueryInput
// =============================================================================

export const SearchQueryInputSchema = z.object({
  /** User-supplied search string. */
  query: z.string().min(1).max(500),
  /** Hard limit — max 50 total across all groups. */
  limit: z.number().int().positive().max(50).optional(),
  /** Restrict to a subset of entity types. */
  entity_types: z.array(z.string()).optional(),
});
export type SearchQueryInput = z.infer<typeof SearchQueryInputSchema>;

// =============================================================================
// SearchResponse
// =============================================================================

/**
 * `grouped` is keyed by entity_type (e.g. { user: [...], document: [...] }).
 * The flat `results` array is the same rows ordered by rank DESC for the
 * "flat list" Cmd+/ presentation.
 */
export const SearchResponseSchema = z.object({
  results: z.array(SearchResultSchema),
  grouped: z.record(z.string(), z.array(SearchResultSchema)),
  /** Total rows considered before grouping (may exceed results.length). */
  total: z.number().int().nonnegative(),
  /** Original query string echoed back for client cache keys. */
  query: z.string(),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;
